import { useEffect, useRef, useState } from "react";

import Steps from "./Form/Steps.js";
import First from "./Form/First.js";
import Second from "./Form/Second.js";
import Third from "./Form/Third.js";
import Fourth from "./Form/Fourth.js";
import Fifth from "./Form/Fifth.js";

import Close from "./styles/svg/Close.js";
import {
  State,
  StringMap,
  SendStepData,
  LobbyFiles,
  Mode,
  TheSteps,
  Currencies,
  ModeType,
} from "./types/types.js";
import { Layout, Segment, Item, Heading, Button, Alert, Loader } from "ui-components";

import {
  getRegistrationFormData,
  registerReal,
  registerFun,
  getLocale,
  getLobbyFiles,
  getRequiredFields,
  getUserInfo,
  reportError,
  additionalRegistration,
  validateEmailCode,
  resendVerificationEmail,
} from "./api.js";

import {
  mergeProvinces,
  mergeCurrencies,
  mergeRegions,
  mergeCountryCur,
  getFields,
  range,
  trns,
  insertAfter,
  constructStepParams,
} from "./helper.js";

import PostMessage from "./PostMessage.js";
import * as cons from "./constants.js";
import config from "./config.js";
import useRegStore from "./store/index.js";
import { useShallow } from "zustand/react/shallow";
import ValidationCode from "./Form/ValidationCode.js";

type Props = {
  onSuccess?: (l?: string, p?: string, userId?: number) => Promise<void>;
  btag?: string;
  tracker?: number;
  lang?: string;
  extraFields?: StringMap<any>;
  uname?: string;
  pword?: string;
  hash?: string;
  platformData?: StringMap<string | number>;
  servicePath: string;
  cancel?: () => void;
  additional?: boolean;
  mode: ModeType;
  globalErr?: (e: string) => void;
};

type CommonProps = {
  previousPage: () => void;
  nextPage: () => void;
  saveValues: (
    state: StringMap<string | number>,
    errors?: StringMap<string> | null,
    e?: () => void
  ) => void;
  required: StringMap<boolean>;
  optional: StringMap<boolean>;
  setDirty: (fieldname: string) => void;
  dirty: StringMap<boolean>;
  errors: StringMap<string>;
  clearError: (fieldname: string) => void;
  isFinal: boolean;
  isStart: boolean;
  trns: (ids: string, v: string) => void;
  submitted: boolean;
  inFlight: boolean;
  servicePath: string;
};

const App = (props: Props) => {
  const { mode, cancel, additional, onSuccess } = props;

  const userRef = useRef<string | undefined>(undefined);
  const passRef = useRef<string | undefined>(undefined);
  const uIdRef = useRef<number | undefined>(undefined);

  const applyStyle = (url: string, checksum: string, callback = null) => {
    const csUrl = checksum ? `${url}?checksum=${checksum}` : url;

    let found = document.head.querySelector(`link[href='${csUrl}']`);

    if (!found) {
      const stylesheet = document.createElement("link");
      stylesheet.href = csUrl;
      stylesheet.rel = "stylesheet";
      stylesheet.type = "text/css";

      if (callback) stylesheet.onload = callback;
      const titleTag = document.getElementsByTagName("title")[0];

      // Appending the stylesheet below an element that does not exixt will generate an error.
      if (!titleTag) {
        return;
      } else {
        insertAfter(titleTag, stylesheet);
      }
    }
  };

  useEffect(() => {
    set();
    return () => {
      userRef.current = undefined;
      passRef.current = undefined;
      uIdRef.current = undefined;
      reset()
    };
  }, []);

  const createPath = (servicePath: string, file: StringMap<string>) => {
    return `${servicePath}/${file.dlpath}${file.filename}`;
  };

  const {
    page,
    message,
    error,
    dirty,
    errors,
    inFlight,
    step1,
    step2,
    step3,
    step4,
    step5,
    steps,
    submitted,
    setupInitialVariables,
    setExtraFields,
    setFields,
    prevPage,
    setDirty,
    setErrors,
    setField,
    reqFields,
    optFields,
    reset,
  } = useRegStore(
    useShallow(state => ({
      page: state.page,
      error: state.error,
      message: state.message,
      dirty: state.dirty,
      errors: state.errors,
      inFlight: state.inFlight,
      step1: state.step1,
      step2: state.step2,
      step3: state.step3,
      step4: state.step4,
      step5: state.step5,
      steps: state.steps,
      submitted: state.submitted,
      setupInitialVariables: state.setupInitialVariables,
      setExtraFields: state.setExtraFields,
      setFields: state.setField,
      prevPage: state.prevPage,
      setDirty: state.setDirty,
      setErrors: state.setErrors,
      setField: state.setField,
      reqFields: mode === "real" ? state.real.required : state.fun.required,
      optFields: mode === "fun" ? state.real.optional : state.fun.optional,
      reset: state.reset,
    }))
  );

  const emailValidation = useRegStore(useShallow(state => state.emailValidation));
  const emailVerificationCode = useRegStore(useShallow(state => state.emailVerificationCode));
  const [showEmailVerificationCodeStep, setShowEmailVerificationCodeStep] = useState(false);

  const set = async () => {
    const { mode, lang, hash, servicePath, platformData, additional, globalErr } = props;
    useRegStore.getState().setMode(mode);
    try {
      let { extraFields } = props;
      const locale = !lang ? "en" : lang;
      const translations = await getLocale(servicePath, locale);
      window.trans = translations ? translations : {};

      const use_ssl = servicePath && servicePath.slice(0, 5) === "https" ? 1 : 0;
      let data;
      if (platformData) {
        data = {
          ...platformData,
          lang: locale,
          use_ssl,
        };
      } else {
        data = {
          os: "EmbeddedLobby",
          lang: locale,
          currentVersion: "5",
          use_ssl,
          platform: "unified",
        };
      }
      let resp;

      // Slippery or Additional, either way pull some preexisting user details
      if (hash) {
        // Slippery Registration Fun->Real Prefill cellphone
        let details = await getUserInfo(hash, servicePath);
        useRegStore
          .getState()
          .appendedExistingUserData(
            details.cell_phone || "",
            details.country_id || "",
            details.province_id || ""
          );
      }
      if (hash && additional) {
        //Adidtional
        resp = await getRegistrationFormData(data, servicePath);
        let missingFields = await getRequiredFields(hash, servicePath, "signup", true);
        extraFields = {
          required: extraFields
            ? [...extraFields.required, ...missingFields["missing"]["required"]]
            : missingFields["missing"]["required"],
          optional: extraFields ? [...extraFields.optional] : [],
        };
      } else if (hash && !extraFields) {
        //Slippery
        resp = await getRequiredFields(hash, servicePath, "cashier");
      } else {
        resp = await getRegistrationFormData(data, servicePath);
      }

      const { lobby_files, clientfile_path } = await getLobbyFiles(data, servicePath);

      // Brand specific stylesheet
      const registrationCSS = lobby_files.filter(
        (x: LobbyFiles) => x.filename === cons.REGISTRATION_CSSFILE
      );

      if (registrationCSS !== null && registrationCSS.length > 0) {
        applyStyle(
          createPath(clientfile_path, registrationCSS[0]),
          registrationCSS[0].checksum
        );
      }

      // The registration component's base stylesheet (registration.css) is now
      // bundled into the build (standalone) and into the lobby's lazy-loaded
      // registration chunk, so it is no longer loaded as a runtime clientfile.
      // Only the per-casino brand override (registration_brand.css) above
      // remains a clientfile.

      const { fields, ...rest } = resp;

      // Collect regions into map keyed on id for quick matching, this object is never very large
      rest.regions = mergeRegions(rest.regions);

      // Real signup is union of real required and fun required
      if (fields.real.required && fields.fun.optional) {
        fields.real.required = [...fields.real.required, ...fields.fun.required];
        fields.real.optional = [...fields.real.optional, ...fields.fun.optional];
      }

      // Same idea. What we're doing here is keying the provinces on the country id,
      // so we can quickly generate the select field
      // Without this, it will be O(n) time for each selection on province/country

      if (rest.provinces) {
        rest.provinces = mergeProvinces(rest.provinces);
      }

      const currencies: StringMap<Currencies> = rest.currencies
        ? mergeCurrencies(rest.currencies)
        : {}; // need better failover

      if (rest.country_currencies) {
        rest.country_currencies = mergeCountryCur(rest.country_currencies, currencies);
      }

      // Get the number of total number of steps in the state, regardless of what is required/optional
      const totalSteps =
        Object.keys(useRegStore.getState()).filter(k => k.startsWith("step")).length - 1;

      const newSteps = extraFields
        ? buildSteps(extraFields.required, extraFields.optional, totalSteps)
        : buildSteps(fields[mode].required, fields[mode].optional, totalSteps);
      const newCurrencies = Object.keys(currencies).map((x: string) => currencies[x]);

      setupInitialVariables(
        newCurrencies,
        newSteps,
        rest.countries,
        rest.country_currencies,
        rest.countrycodes,
        rest.default_currency,
        rest.download_url,
        rest.provinces,
        rest.regions,
        rest.tc_markdown,
        rest.title_ids,
        rest.titles,
        rest.emailVerification ?? false,
        rest.emailVerificationCode ?? false,
      );

      if (extraFields) {
        Object.keys(extraFields).forEach(k => {
          if (extraFields !== undefined && extraFields[k] !== undefined) {
            extraFields[k] = extraFields[k].reduce(constructStepParams, {});
          }
        });
        setExtraFields(mode, extraFields);
      } else if (fields) {
        // Convert array of fields to map for O(1) access
        Object.keys(fields[mode]).forEach(k => {
          fields[mode][k] = fields[mode][k].reduce(constructStepParams, {});
        });
        setFields(fields);
        const { optional } = useRegStore.getState()[mode];
        if (optional.province_id) {
          useRegStore.getState().setProvinceReq(mode);
        }
      }

      PostMessage.regLoaded(mode);
    } catch (e) {
      if (globalErr) {
        globalErr(e);
      } else {
        console.log(e);
        const { setError, resetInflight } = useRegStore.getState();
        setError(
          "danger",
          trns(
            "IDS_LOGINBOX_REGISTRATION_ERROR",
            "There has been a critical registration error, please contact support."
          )
        );
        resetInflight();

        let loginHash: string | null = hash ? hash : null;
        const report = await reportError(
          loginHash,
          "Registration",
          "Unknown Registration Error",
          e.toString(),
          servicePath
        );

        console.log(e, report);
      }
    }
  };

  const buildSteps = (
    reqFields: string[],
    optFields: string[] = [],
    num_steps: number = 5
  ): string[] =>
    range(1, num_steps + 1)
      .map((i: number) => (!isDisjoint(i, reqFields, optFields) ? `step${i}` : null))
      .flatMap(f => {
        return f ? [f] : [];
      });

  // Should the step be shown at all? If the set of required fields and the set of fields in
  // the step is disjoint then we shouldn't show that step.
  const isDisjoint = (stepNum: number, req: string[] = [], opt: string[] = []) => {
    const regStore = useRegStore.getState();
    const step = regStore[`step${stepNum}` as keyof typeof TheSteps];
    let includeStep = true;
    Object.keys(step).forEach(e => {
      if (req.includes(e) || opt.includes(e)) includeStep = false;
    });
    return includeStep;
  };

  const resendCode = async () => {
    const { servicePath } = props;
    const { setInflight, resetInflight, setError, step1 } = useRegStore.getState();
    if (step1.email === null) return;
    try {
      setInflight();
      await resendVerificationEmail(step1.email, servicePath);
      resetInflight();
    } catch (e) {
      resetInflight();
      setError(
        "danger",
        trns(
          "IDS_EMAIL_VERIFICATION_RESEND_ERROR",
          "There was an error resending the verification email, please try again later."
        )
      );
    }
  }

  const submit = async () => {
    const appState = useRegStore.getState();
    const { setInflight, resetInflight, setError, page, fun, real, ...data } = appState;
    const {
      mode,
      uname,
      pword,
      extraFields,
      servicePath,
      btag,
      tracker,
      lang,
      hash,
      additional,
      onSuccess,
    } = props;

    setInflight();

    const collectSendData = (): SendStepData => {
      const { required, optional } = appState[mode];
      const collectedSendData = data.steps.reduce<SendStepData>(
        (acc, step, _i) => {
          if (step in appState) {
            const fields = getFields(
              appState[step as keyof State] as StringMap<any>,
              required,
              optional
            );
            return { ...fields, ...acc };
          }
          return acc;
        },
        {
          banner_id: btag || config.btag || undefined,
          tracker: tracker || config.tracker,
          lang: !lang ? "en" : lang,
        }
      );
      return collectedSendData;
    };

    const trimObjectStrings = (obj: SendStepData): SendStepData => Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        typeof value === "string" ? value.trim() : value,
      ])
    ) as SendStepData;

    const send = trimObjectStrings(collectSendData());

    if (
      appState.step5.cell_phone_edit !== appState.step2.cell_phone &&
      appState.step5.phone_verification
    ) {
      send.cell_phone = appState.step5.cell_phone_edit;
    }

    if (uname && pword) {
      send.login = uname;
      send.password = pword;
    }

    if (appState.step1.login) {
      userRef.current = appState.step1.login;
    }

    if (appState.step1.password) {
      passRef.current = appState.step1.password;
    }

    if (hash) send.hash = hash;

    if (extraFields) send.funToReal = true; // send this to backend

    try {
      let resp: StringMap<any>;

      if (additional && hash && !extraFields) {
        PostMessage.submitReg();
        resp = await additionalRegistration(send, servicePath);
        uIdRef.current = resp.userId || undefined;
        setError("success", "additional registration completed");
        resetInflight();

        if (emailValidation && appState.step1.email) {
          await resendVerificationEmail(appState.step1.email, servicePath, false);
        }

        onSuccess && (await onSuccess(userRef.current, passRef.current, uIdRef.current));
        userRef.current = undefined;
        passRef.current = undefined;
        uIdRef.current = undefined;
        reset();
        return;
      } else if (extraFields || mode === "real") {
        resp = await registerReal(send, servicePath);
      } else {
        resp = await registerFun(send, servicePath);
      }
      uIdRef.current = resp.userId || undefined;

      PostMessage.regComplete();

      if (emailValidation && emailVerificationCode) {
        resetInflight();
        setShowEmailVerificationCodeStep(true);
        return;
      }

      // Call complete registration in
      setError("success", send.login ? `${resp.info} username ${send.login}` : `${resp.info}`);
      resetInflight();
      // backend likes returning user_id as username
      onSuccess && (await onSuccess(userRef.current, passRef.current, uIdRef.current));

      userRef.current = undefined;
      passRef.current = undefined;
      uIdRef.current = undefined;
      reset();
    } catch (e) {
      PostMessage.regError(e.code);
      const { setPage, appendError, resetInflight } = useRegStore.getState();
      resetInflight();
      switch (e.code) {
        case cons.REGISTER_REQUIRE_EMAIL:
          setPage(1);
          appendError(
            "email",
            trns("IDS_LOGINBOX_EMAIL_REQUIRED", "An email address is required")
          );
          setError(
            "danger",
            trns("IDS_LOGINBOX_EMAIL_REQUIRED", "An email address is required")
          );
          break;
        case cons.PASSWORD_SIMPLE:
          setPage(1);
          appendError("password", trns("IDS_PASSWORD_SIMPLE", "Password is too simple"));
          break;
        case cons.INCORRECT_PASSWORD:
          setPage(1);
          appendError("password", trns("IDS_PASSWORD_SIMPLE", "Password is too simple"));
          break;
        case cons.LOGIN_NA:
          const setUserSuggestions = useRegStore.getState().setUserSuggestions;
          setUserSuggestions(e.login_suggestions);
          setPage(1);
          appendError(
            "login",
            trns("IDS_LOGINBOX_LOGIN_IN_USE", "The login you have chose is already in use.")
          );
          break;
        case cons.INVALID_LOGIN_CHAR:
          setPage(1);
          appendError(
            "login",
            trns(
              "IDS_LOGINBOX_LOGIN_INVALID_CHAR",
              "Invalid characters, only letters, numbers and underscore/dash allowed."
            )
          );
          break;
        case cons.INVALID_LOGIN_LENGTH:
          setPage(1);
          appendError(
            "login",
            trns("IDS_LOGINBOX_LOGIN_LENGTH", "Login must be between 5 and 12 characters")
          );
          break;
        case cons.INVALID_EMAIL:
          setPage(1);
          appendError(
            "email",
            trns(
              "IDS_LOGINBOX_ENTER_EMAIL_INVALID",
              "Your email address appears to be invalid."
            )
          );
          break;
        case cons.ALREADY_REG:
          setPage(1);
          appendError(
            "email",
            trns(
              "IDS_LOGINBOX_EMAIL_ALREADY_REG",
              "Your email address indicates you may already be registered"
            )
          );
          break;
        case cons.SHORT_PASSWORD:
        case cons.LONG_PASSWORD:
          setPage(1);
          appendError(
            "password",
            trns("IDS_LOGINBOX_PASSWORD_MIN_REQ", "Password must be at least 5 characters.")
          );
          break;
        case cons.TOO_YOUNG:
          setError(
            "danger",
            trns(
              "IDS_TOO_YOUNG_CONTACT_SUPPORT",
              `We're sorry, you are too young to enter the casino.
            Please contact customer service if this was in error.
            \n You may be required to submit proper age verification documents.`
            )
          );
          break;
        case cons.FIRST_NAME:
          setPage(2);
          appendError(
            "fname",
            trns("IDS_LOGINBOX_ENTER_FIRST_NAME", "You must enter your first name.")
          );
          break;
        case cons.LAST_NAME:
          setPage(2);
          appendError(
            "lname",
            trns("IDS_LOGINBOX_ENTER_LAST_NAME", "You must enter your last name.")
          );
          break;
        case cons.INVALID_COUNTRY:
          setPage(2);
          appendError(
            "country_id",
            trns("IDS_LOGINBOX_ENTER_COUNTRY", "You must enter your country.")
          );
          break;
        case cons.INVALID_BDAY:
          setPage(2);
          appendError(
            "year",
            trns("IDS_LOGINBOX_ENTER_BIRTHDAY", "You must enter your full birth date.")
          );
          break;
        case cons.COUNTRY_BLOCKED:
          setError(
            "danger",
            trns(
              "IDS_LOGINBOX_COUNTRY_PROHIBITED",
              "We currently prohibit registration requests from your location."
            )
          );
          break;
        case cons.INVALID_CASINO:
          setError(
            "danger",
            trns(
              "IDS_LOGINBOX_INVALID_CASINO_FOUND",
              "There has been an error with registration. Invalid casino found."
            )
          );
          break;
        case cons.REGISTRATION_FAILED:
          setError(
            "danger",
            trns(
              "IDS_LOGINBOX_REG_FAILED_CONTACT_SUPPORT",
              "Registration of your account has failed. Contact support."
            )
          );
          resetInflight();
          break;
        default:
          setError(
            "danger",
            trns(
              "IDS_LOGINBOX_REGISTRATION_ERROR",
              "There has been a critical registration error, please contact support."
            )
          );
          break;
      }
    }
  };

  const validateCode = async (code: string): Promise<boolean> => {
    const { servicePath } = props;
    const { setInflight, resetInflight, setError, step1 } = useRegStore.getState();
    if (!step1.email) return false;
    try {
      setInflight();
      const resp = await validateEmailCode(step1.email, code, servicePath);

      resetInflight();
      if (resp.validated) {
        PostMessage.regEmailCodeValidationSuccess();
        return true;
      } else {
        setError(
          "danger",
          trns(
            "IDS_EMAIL_VALIDATION_CODE_INVALID",
            "The email validation code you have entered is invalid, please try again."
          )
        );
        return false;
      }
    } catch (e) {
      PostMessage.regEmailCodeValidationFailed(e.code);
      resetInflight();
      setError(
        "danger",
        trns(
          "IDS_EMAIL_VALIDATION_CODE_ERROR",
          "There was an error validating your email code, please try again."
        )
      );
      return false;
    }
  }

  const showStep = (stepNumber: number) => steps[page - 1] === `step${stepNumber}`;
  const isFinal = () => steps.length === page;
  const isStart = () => steps[page - 1] === steps[0];
  const saveValues = (
    fieldValues: StringMap<string | number>,
    newErrors: StringMap<string> | null = null,
    cb?: () => void
  ) => {
    if (newErrors) {
      setField(fieldValues, newErrors);
      cb && cb();
    } else {
      setField(fieldValues);
      cb && cb();
    }
  };

  const nextPage = () => {
    const { nextPage } = useRegStore.getState();
    if (page === steps.length) {
      submit();
    } else {
      nextPage();
    }
  };

  const clearError = (fieldname: string) => {
    const removed = Object.keys(errors).reduce(
      (acc, e) => (errors[fieldname] ? { ...acc } : { ...acc, e: errors[e] }),
      {}
    );
    setErrors(removed);
  };

  const regFooter = (
    <Segment className="reg-footer">
      {trns("IDS_LOGINBOX_HAVE_AN_ACCOUNT", "Have an account?")}{" "}
      <button onClick={cancel}>{trns("IDS_LOGINBOX_SIGN_IN", "Sign In")}</button>
    </Segment>
  );

  const commonProps: CommonProps = {
    previousPage: prevPage,
    nextPage: nextPage,
    saveValues: saveValues,
    required: reqFields,
    optional: optFields,
    setDirty: setDirty,
    dirty,
    errors,
    clearError: clearError,
    isFinal: isFinal(),
    isStart: isStart(),
    trns: trns,
    submitted,
    inFlight,
    servicePath: props.servicePath,
  };

  return (
    <Segment className="reg-outer-container">
      <Segment className="reg-inner-container">
        <Segment className="reg-header x">
          <Item>
            <Heading headingLevel="h1" className="reg-title">
              {props.mode === "real"
                ? trns("IDS_LOGINBOX_ACCOUNT_REGISTRATION_REAL", "Real Account Registration")
                : trns("IDS_LOGINBOX_ACCOUNT_REGISTRATION_FUN", "Fun Account Registration")}
            </Heading>

            <Heading headingLevel="h2" className="reg-sub-title">
              {props.extraFields &&
                props.extraFields.required &&
                Object.keys(props.extraFields.required).length > 0
                ? trns(
                  "IDS_REG_MORE_DETAILS",
                  "Please provide us with a few more details to complete your registration"
                )
                : ""}
            </Heading>
          </Item>
          {!additional && (
            <Item className="xlast">
              <Button
                asLink
                onClick={() => {
                  reset();
                  set();
                  cancel && cancel();
                }}
              >
                <Close fill="#fff" />
              </Button>
            </Item>
          )}
        </Segment>

        {message !== null && <Alert type={error}>{message}</Alert>}

        <Layout grid>
          <Layout.Segment col="12">
            {inFlight && <Loader fill="#363636" useSVG containedOverlay />}
            <Segment className="reg-form">
              {showStep(1) && !showEmailVerificationCodeStep && (
                <First
                  {...commonProps}
                  saved={step1}
                  cancel={() => {
                    if (window.exitRegistration) window.exitRegistration();
                    cancel && cancel();
                  }}
                />
              )}
              {showStep(2) && !showEmailVerificationCodeStep && <Second {...commonProps} saved={step2} />}
              {showStep(3) && !showEmailVerificationCodeStep && <Third {...commonProps} saved={step3} />}
              {showStep(4) && !showEmailVerificationCodeStep && <Fourth {...commonProps} saved={step4} />}
              {showStep(5) && !showEmailVerificationCodeStep && <Fifth {...commonProps} saved={step5} />}

              {showEmailVerificationCodeStep && <ValidationCode
                validateCode={validateCode}
                resend={resendCode}
                onSuccess={async () => {
                  if (onSuccess) {
                    await onSuccess(userRef.current, passRef.current, uIdRef.current)
                    userRef.current = undefined;
                    passRef.current = undefined;
                    uIdRef.current = undefined;
                    useRegStore.getState().reset();
                  }
                }} />
              }
            </Segment>
            <Steps activeStep={page} totalSteps={steps.length} />
            {!additional && regFooter}
          </Layout.Segment>
        </Layout>
      </Segment>
    </Segment>
  );
};

export default App;